import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LayoutGrid } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { DeviceScreen } from "@/components/device-screen";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Spinner } from "@/components/ui/spinner";
import { useDeviceSession } from "@/hooks/use-device-session";
import { useDeviceStream } from "@/hooks/use-device-stream";
import { useReservationRenewal } from "@/hooks/use-reservation-renewal";
import { trpc } from "@/lib/trpc";
import type { DeviceListItem } from "@/lib/types";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/my-phones")({
  component: MyPhonesPage,
});

function MyPhonesPage() {
  const { user } = Route.useRouteContext();
  const { pollInterval } = useDeviceStream();
  const devices = useQuery({
    ...trpc.device.list.queryOptions(),
    refetchInterval: pollInterval,
  });
  const [selected, setSelected] = useState<string | null>(null);
  const mine = (devices.data ?? []).filter((d) => d.reservation?.userId === user.id);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="My phones" meta={`${mine.length} leased`} />
      <p className="text-copy-14 text-gray-900">
        All your leased phones, live. Select a screen to control it, then tap, swipe or type. Only
        the selected phone receives your input. Audio is muted.
      </p>
      {devices.isPending ? (
        <div className="flex justify-center p-12">
          <Spinner />
        </div>
      ) : devices.isError ? (
        <EmptyState title="Could not load your phones" description={devices.error.message}>
          <Button onClick={() => devices.refetch()}>Try again</Button>
        </EmptyState>
      ) : mine.length === 0 ? (
        <EmptyState
          icon={<LayoutGrid />}
          title="No phones leased yet"
          description="Lease phones from Devices and their live screens will appear here."
        >
          <Button asChild>
            <Link to="/devices">Browse devices</Link>
          </Button>
        </EmptyState>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,280px),1fr))] gap-4">
          {mine.map((device) => (
            <PhoneTile
              key={device.reservation?.id}
              device={device}
              selected={selected === device.id}
              onSelect={() => setSelected(device.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PhoneTile({
  device,
  selected,
  onSelect,
}: {
  device: DeviceListItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const session = useDeviceSession(device.id, canvasRef, true);
  const queryClient = useQueryClient();
  // Viewing renews the lease, but activity on another tile must not defeat this
  // phone's idle policy. The provider reports actual input per device.
  const renewal = useReservationRenewal(
    session.revoked ? undefined : device.reservation?.id,
    device.reservation?.expiresAt,
    undefined,
    false,
  );
  useEffect(() => {
    if (session.revoked) {
      void queryClient.invalidateQueries({ queryKey: trpc.device.list.queryKey() });
    }
  }, [session.revoked, queryClient]);

  const name = device.name || device.model || device.id;
  const ready = session.state === "open" && !session.revoked;
  const press = (key: string) => {
    session.send({ type: "key", key, down: true });
    session.send({ type: "key", key, down: false });
  };

  return (
    <section
      aria-label={name}
      className={cn(
        "min-w-0 rounded-lg border bg-background-100 p-3",
        selected ? "border-mine ring-1 ring-mine" : "border-gray-alpha-400",
      )}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="truncate font-medium text-label-14" title={name}>
          {name}
        </h2>
        <Button asChild variant="tertiary" size="sm">
          <Link to="/devices/$deviceId" params={{ deviceId: device.id }}>
            Open
          </Link>
        </Button>
      </div>
      <div className="relative h-[360px]">
        <DeviceScreen
          session={session}
          canvasRef={canvasRef}
          platform={device.platform}
          interactive={selected && ready}
          className="h-full"
        />
        {!selected && (
          <button
            type="button"
            aria-label={`Control ${name}`}
            className="absolute inset-0 flex items-end justify-center rounded-md pb-3 focus-visible:outline-2 focus-visible:outline-ring"
            onClick={() => {
              onSelect();
              // Selection must never forward the selecting click as a phone tap.
              requestAnimationFrame(() => canvasRef.current?.focus());
            }}
          >
            <span className="rounded-full bg-black/75 px-3 py-1 text-label-13 text-white">
              Select to control
            </span>
          </button>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {device.platform === "android" && (
          <Button
            size="sm"
            variant="secondary"
            disabled={!selected || !ready}
            onClick={() => press("Back")}
          >
            Back
          </Button>
        )}
        <Button
          size="sm"
          variant="secondary"
          disabled={!selected || !ready}
          onClick={() => press("Home")}
        >
          Home
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!selected || !ready}
          onClick={() => press("AppSwitch")}
        >
          {device.platform === "android" ? "Recents" : "App switcher"}
        </Button>
        <span className="text-gray-900 text-label-12">{selected ? "Controlling" : "Viewing"}</span>
      </div>
      {device.reservation && (
        <p className="mt-3 text-copy-13 text-gray-900">
          Idle phones are released by your farm’s policy.{" "}
          <button
            type="button"
            className="underline"
            disabled={session.revoked}
            onClick={renewal.keepAlive}
          >
            Keep this phone
          </button>
        </p>
      )}
      {renewal.failed && (
        <p role="status" className="mt-2 text-copy-13 text-amber-900">
          Could not renew this lease. It may expire.
        </p>
      )}
      {!!device.reservation?.observers.length && (
        <p className="mt-2 text-copy-13 text-gray-900">
          Also in this session:{" "}
          {device.reservation.observers.map((o) => o.name ?? "Another user").join(", ")}
        </p>
      )}
      {!!device.reservation?.joinRequests.length && (
        <Link
          className="mt-2 block text-copy-13 underline"
          to="/devices/$deviceId"
          params={{ deviceId: device.id }}
        >
          Review requests to join
        </Link>
      )}
    </section>
  );
}
